/*******************************************************************************
 * Copyright (c) 2025 Passport Fintech Inc. All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 ******************************************************************************/

import {
  PaginationInfo,
  PaginationInfoApiResponse,
  paginationInfoFromApiResponse,
} from '@shared/models/pagination-info';
import {
  PspToken,
  PspTokenApiResponse,
  pspTokenFromApiResponse,
} from '@shared/models/psp-token';
import {Role, RoleApiResponse, roleFromApiResponse} from '@shared/models/roles';

// Users info
export interface IamUserInfos {
  userInfos: IamUserInfo[];
}

export interface IamUserInfosApiResponse {
  user_infos: IamUserInfoApiResponse[];
}

export const iamUserInfosFromApiResponse = (
  response: IamUserInfosApiResponse
): IamUserInfos => {
  return {
    userInfos: response.user_infos.map(userInfo => iamUserInfoFromApiResponse(userInfo)),
  };
};

export interface IamUserInfo {
  userId: string;
  accountId: string;
  accountName: string;
  product: string;
}

export interface IamUserInfoApiResponse {
  user_id: string;
  account_id: string;
  account_name: string;
  product: string;
}

export const iamUserInfoFromApiResponse = (response: IamUserInfoApiResponse): IamUserInfo => {
  return {
    userId: response.user_id,
    accountId: response.account_id,
    accountName: response.account_name,
    product: response.product,
  };
};

export interface IamUserResponse {
  accessToken: PspToken;
  needsNda: string;
  ndaSigned: string;
  isActive: string;
}

export interface IamUserResponseApiResponse {
  access_token: PspTokenApiResponse;
  needs_nda: string;
  nda_signed: string;
  is_active: string;
}

export const iamUserResponseFromApiResponse = (
  response: IamUserResponseApiResponse
): IamUserResponse => {
  return {
    accessToken: pspTokenFromApiResponse(response.access_token),
    needsNda: response.needs_nda,
    ndaSigned: response.nda_signed,
    isActive: response.is_active,
  };
};

export interface IamUser {
  userId: string;
  resourceName: string;
  firstName: string;
  lastName: string;
  email: string;
  accountId: string;
  mobilePhoneNumber: string;
  createdAt: string;
  updatedAt: string;
  product: string;
  roles: Role[];
  loginId?: string;
}

/**
 * This interface describes how the UserApiResponse is returned by the API,
 * so we can leverage the strict typing capabilities and not rely on "any"
 */
export interface IamUserApiResponse {
  user_id: string;
  resource_name: string;
  first_name: string;
  last_name: string;
  email: string;
  account_id: string;
  mobile_phone_number: string;
  created_at: string;
  updated_at: string;
  product: string;
  roles: RoleApiResponse[];
  login_id?: string;
}

/**
 * This method transforms the UserApiResponse into our own
 * User object which our application understands
 * @param response the pagination API response
 * @returns the deserialized User
 */
export const iamUserFromApiResponse = (response: IamUserApiResponse): IamUser => {
  return {
    userId: response.user_id,
    resourceName: response.resource_name,
    firstName: response.first_name,
    lastName: response.last_name,
    email: response.email,
    accountId: response.account_id,
    mobilePhoneNumber: response.mobile_phone_number,
    createdAt: response.created_at,
    updatedAt: response.updated_at,
    product: response.product,
    roles: response.roles.map((roleResponse: RoleApiResponse) =>
      roleFromApiResponse(roleResponse)
    ),
    ...(response.login_id && {loginId: response.login_id}),
  };
};

export interface IamUsersPagination {
  iamUsers: IamUser[];
  pagination: PaginationInfo;
}

/**
 * This interface describes how the IamUsersPaginationApiResponse is returned by the API,
 * so we can leverage the strict typing capabilities and not rely on "any"
 */
export interface IamUsersPaginationApiResponse {
  users: Array<IamUserApiResponse>;
  pagination_info: PaginationInfoApiResponse;
}

/**
 * This method transforms the IamUsersPaginationApiResponse into our own
 * IamUsersPagination object which our application understands
 * @param response the pagination API response
 * @returns the deserialized IamUserPagination
 */
export const iamUsersPaginationFromResponse = (
  response: IamUsersPaginationApiResponse
): IamUsersPagination => {
  return {
    iamUsers: response.users.map((iamUserResponse: IamUserApiResponse) =>
      iamUserFromApiResponse(iamUserResponse)
    ),
    pagination: paginationInfoFromApiResponse(response.pagination_info),
  };
};
